import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ExternalLink,
  Clock,
  Download,
  FileOutput,
  FileText,
  Gavel,
  Link2,
  Trash2,
  UserCheck,
  Users,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type DecisionExportFormat,
  type DecisionQuestionView,
  type DecisionSheetStatus,
  type DecisionSheetView,
  type DocumentDetail,
} from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { useConfirmPopover } from '../../../components/ui/confirm-popover';
import { GlassSegmented } from '../../../components/ui/glass-segmented';
import { ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { ApiRequestError } from '../../../lib/http';
import { orgTimezone } from '../../../lib/org-timezone';
import { documentApi, documentKeys } from '../../documents/api';
import { DocumentPreviewDialog } from '../../documents/components/document-preview-dialog';
import { projectFilesKeys } from '../../files/api';
import { decisionSheetApi, decisionSheetKeys } from '../api';
import { readAnswerConflict, readNotClosed, readNotReady, type SheetNotReady } from '../errors';
import { sheetDocLabels, sheetToExportFile } from '../export-doc';
import { exportSheetToDocument, previousExportId, SheetNotClosedError } from '../export-flow';
import { downloadDecisionFile, sheetViewToFile } from '../file';
import {
  DECISION_FILTERS,
  type DecisionFilter,
  decisionSections,
  decisionTotals,
  linkRespondents,
  stampDateTime,
  stampTime,
} from '../model';
import { AnswerHistoryDialog } from './answer-history-dialog';
import { DecidersDialog } from './deciders-dialog';
import { OpenProblemsDialog } from './open-problems-dialog';
import { DecisionSheetEditor, SheetMetaEditor } from './decision-sheet-editor';
import { DecisionQuestionCard } from './decision-question-card';
import { DecisionResult, DecisionTotalsBar } from './decision-summary';
import {
  ExportFormatDialog,
  ExportNotClosedDialog,
  type ExportStamp,
} from './export-format-dialog';
import { OriginPill, RecommendedPill, SheetStatusPill } from './labels';
import { AssignAnswerTaskDialog } from './assign-task-dialog';
import { RaidRecordDialog } from './raid-record-dialog';
import { type SheetAction, SheetActionBar } from './sheet-actions';
import { DecisionSheetTaskList } from './sheet-tasks';
import { DecisionGroupHeading } from './group-heading';

/** Cập nhật trực tiếp — plan §4.3. Chưa cần SSE. */
export const DECISION_REFETCH_MS = 15_000;

const RESULT_ANCHOR = 'decision-sheet-result';

/** Hai chế độ của trang sổ — APM-DSX-e, plan §11.4. Search param `sheetMode`. */
export const DECISION_SHEET_MODES = ['view', 'edit'] as const;
export type DecisionSheetMode = (typeof DECISION_SHEET_MODES)[number];

/** Nút đổi trạng thái theo trạng thái hiện tại — vòng đời plan §4.2. */
const TRANSITIONS: Record<DecisionSheetStatus, readonly ('open' | 'close' | 'reopen')[]> = {
  draft: ['open'],
  open: ['close'],
  closed: ['reopen'],
};

const EXPORT_FORMATS: readonly DecisionExportFormat[] = ['chilldoc', 'md'];

/** Lần xuất gần nhất đọc từ chính tài liệu — phiên bản mới nhất đứng đầu (`DocumentRepository.versions`). */
function exportStampOf(document: DocumentDetail | undefined): ExportStamp | undefined {
  if (!document) return undefined;
  return {
    at: document.versions[0]?.createdAt ?? document.updatedAt,
    version: document.latestVersionNo,
  };
}

function isGone(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 404;
}

type AnswerVariables =
  | {
      readonly kind: 'answer';
      readonly question: DecisionQuestionView;
      readonly key: string;
      readonly note: string;
    }
  | { readonly kind: 'clear'; readonly question: DecisionQuestionView };

/**
 * Trang một sổ chốt phương án — APM-DSHEET-c. Mở qua `?sheet=<uuid>` ở tab
 * Tổng quan (như trang Files: "trang con, có nút quay lại").
 *
 * ## `asOf` — luật #7
 *
 * Sổ là dữ liệu biến thiên theo thời gian (đáp án đổi, người chốt đổi), nên
 * trang nhận `asOf` và đưa thẳng vào query: có mốc ⇒ API dựng sổ từ event
 * (APM-DSHEET-b), trang chỉ đọc, không refetch. Lối vào là "Xem sổ lúc này"
 * trong Lịch sử một câu.
 *
 * ## Xem | Sửa — APM-DSX-e
 *
 * Hai chế độ trên cùng một trang, công tắc ở đầu trang (`mode` — search
 * param `sheetMode`). **Xem** là sổ như người chốt thấy: đọc, chốt, lọc,
 * tổng — không nút soạn nào lẫn vào. **Sửa** là trình soạn tại chỗ, cùng bố
 * cục. "Sửa" chỉ có khi `can.edit`; sổ `draft` mở mặc định ở Sửa; sổ đã đóng
 * thì "Sửa" khoá (mọi lệnh sửa bị `assertSheetOpen` chặn); xem quá khứ thì chỉ
 * có Xem — không ai sửa được một mốc đã qua.
 *
 * ## Ai chọn được
 *
 * `view.can.answer` do server tính (là người chốt + `document:read`); giao
 * diện chỉ khoá **thêm** khi sổ không mở hay đang xem quá khứ — không bao giờ
 * mở cửa mà server đóng.
 */
export function DecisionSheetPage({
  projectId,
  sheetId,
  asOf,
  mode,
  onModeChange,
  previewDoc,
  onPreviewDocChange,
  onBack,
  onAsOfChange,
  onOpenRaid,
  standalone = false,
  beforeQuestions,
}: {
  readonly projectId: string;
  readonly sheetId: string;
  /** ISO — xem sổ tại mốc này. */
  readonly asOf?: string | undefined;
  /** Chế độ đã chọn; bỏ trống ⇒ theo trạng thái sổ (`draft` ⇒ Sửa). */
  readonly mode?: DecisionSheetMode | undefined;
  /** Có ⇒ chế độ nằm ở URL (cha giữ); không ⇒ trang tự giữ. */
  readonly onModeChange?: ((mode: DecisionSheetMode) => void) | undefined;
  /** Tài liệu đã xuất đang mở xem trước (`?doc=`, APM-PVLINK) — như `mode`: cha giữ thì ở URL. */
  readonly previewDoc?: string | undefined;
  /** Có ⇒ tài liệu đang xem nằm ở URL (cha giữ); không ⇒ trang tự giữ (cửa sổ riêng). */
  readonly onPreviewDocChange?: ((documentId: string | undefined) => void) | undefined;
  readonly onBack: () => void;
  readonly onAsOfChange?: ((asOf: string | undefined) => void) | undefined;
  /** Go to the project's RAID tab — the link on a recorded question (APM-DSHEET-f). */
  readonly onOpenRaid?: (() => void) | undefined;
  /**
   * Cửa sổ riêng `/sheet/$sheetId` — chỉ chế độ Xem, không nút quay lại, công tắc
   * hay hàng nút quản trị: sổ như người trả lời thấy, trong khung giao diện của
   * trang chia sẻ (người dùng 2026-10-07).
   */
  readonly standalone?: boolean;
  /** Chèn ngay trên thanh tổng ở chế độ Xem — cửa sổ riêng đặt khối "Bạn là ai?" ở đây. */
  readonly beforeQuestions?: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<DecisionFilter>('all');
  const [saving, setSaving] = useState<ReadonlySet<string>>(new Set());
  const [conflicts, setConflicts] = useState<Readonly<Record<string, number>>>({});
  const [historyOf, setHistoryOf] = useState<DecisionQuestionView | null>(null);
  const [editingDeciders, setEditingDeciders] = useState(false);
  /** "Mở sổ" khi chưa có người trả lời ⇒ chọn người trả lời trước, xong mở luôn. */
  const [openAfterDeciders, setOpenAfterDeciders] = useState(false);
  /** Lý do server trả khi "Mở sổ" chưa được — hộp "Chưa mở được sổ". */
  const [openProblems, setOpenProblems] = useState<SheetNotReady | null>(null);
  /** Chế độ khi cha không giữ (`onModeChange` bỏ trống). */
  const [localMode, setLocalMode] = useState<DecisionSheetMode | undefined>(undefined);
  const requestedMode = onModeChange ? mode : localMode;
  const setMode = onModeChange ?? setLocalMode;
  /** The exported project document open in the preview dialog (APM-DSHEET-e) — its id. */
  const [localExport, setLocalExport] = useState<string | undefined>(undefined);
  const viewingExport = onPreviewDocChange ? previewDoc : localExport;
  const setViewingExport = onPreviewDocChange ?? setLocalExport;
  /** Hộp chọn định dạng xuất (APM-DSX). */
  const [choosingFormat, setChoosingFormat] = useState(false);
  /** Sổ chưa đóng khi bấm xuất — hộp "Chưa xuất được tài liệu" (APM-DSX). */
  const [exportBlocked, setExportBlocked] = useState<'draft' | 'open' | null>(null);
  const confirm = useConfirmPopover();
  /** "Ghi vào RAID" dialog (APM-DSHEET-f). */
  const [recordingRaid, setRecordingRaid] = useState(false);
  /** "Tạo việc" dialog (APM-DSX-d). Chia sẻ nay nằm trong "Chọn người trả lời". */
  const [assigning, setAssigning] = useState(false);

  const detailKey = decisionSheetKeys.detail(sheetId, asOf);
  const sheet = useQuery({
    queryKey: detailKey,
    queryFn: () => decisionSheetApi.get(sheetId, asOf),
    // Quá khứ không đổi nữa; hiện tại thì người chốt khác đang chọn song song.
    // React Query mặc định không chạy interval khi tab ẩn — đúng "khi tab đang hiện".
    refetchInterval: asOf ? false : DECISION_REFETCH_MS,
    refetchOnWindowFocus: !asOf,
  });

  /*
    The exported documents — one per format (APM-DSX) — for "Exported at … ·
    Open document" (APM-DSHEET-e) and the format dialog. Share the cache key
    with the preview dialog. A soft-deleted document is a 404: the line then
    says so, and the next export of that format creates a new document.
  */
  const exportIds: Record<DecisionExportFormat, string | null> = {
    chilldoc: asOf || !sheet.data ? null : previousExportId(sheet.data, 'chilldoc'),
    md: asOf || !sheet.data ? null : previousExportId(sheet.data, 'md'),
  };
  const exportedQuery = (documentId: string | null) => ({
    queryKey: documentKeys.detail(documentId ?? ''),
    queryFn: () => documentApi.detail(documentId!),
    enabled: documentId !== null,
    retry: (count: number, error: unknown) => !isGone(error) && count < 2,
  });
  const exported = {
    chilldoc: useQuery(exportedQuery(exportIds.chilldoc)),
    md: useQuery(exportedQuery(exportIds.md)),
  };

  const patchQuestion = (questionId: string, patch: Partial<DecisionQuestionView>): void => {
    queryClient.setQueryData<DecisionSheetView>(detailKey, (old) =>
      old
        ? {
            ...old,
            questions: old.questions.map((question) =>
              question.id === questionId ? { ...question, ...patch } : question,
            ),
          }
        : old,
    );
  };

  const markSaving = (questionId: string, on: boolean): void =>
    setSaving((previous) => {
      const next = new Set(previous);
      if (on) next.add(questionId);
      else next.delete(questionId);
      return next;
    });

  const answer = useMutation({
    mutationFn: (variables: AnswerVariables) =>
      variables.kind === 'answer'
        ? decisionSheetApi.answer(sheetId, variables.question.id, {
            key: variables.key,
            note: variables.note,
            baseRev: variables.question.answerRev,
          })
        : decisionSheetApi.clearAnswer(sheetId, variables.question.id, {
            baseRev: variables.question.answerRev,
          }),
    onMutate: (variables) => markSaving(variables.question.id, true),
    onSuccess: (updated, variables) => {
      patchQuestion(variables.question.id, updated);
      void queryClient.invalidateQueries({ queryKey: decisionSheetKeys.list(projectId) });
    },
    onError: (error, variables) => {
      const { question } = variables;
      const conflict = readAnswerConflict(error);
      if (!conflict) {
        toast.error(errorMessage(error, t));
        void queryClient.invalidateQueries({ queryKey: detailKey });
        return;
      }
      // Câu theo đáp án server, nháp của thẻ bỏ đi, rồi nói ai vừa chọn gì.
      patchQuestion(question.id, { answer: conflict.answer, answerRev: conflict.answerRev });
      setConflicts((previous) => ({
        ...previous,
        [question.id]: (previous[question.id] ?? 0) + 1,
      }));
      toast.error(
        conflict.answer
          ? t('decisionSheet.conflictChose', {
              name: conflict.answer.answeredBy?.name ?? t('decisionSheet.someone'),
              key: conflict.answer.key,
              time: stampTime(conflict.answer.answeredAt),
              code: question.code,
            })
          : t('decisionSheet.conflictCleared', { code: question.code }),
      );
    },
    onSettled: (_data, _error, variables) => markSaving(variables.question.id, false),
  });

  const transition = useMutation({
    mutationFn: (action: 'open' | 'close' | 'reopen') =>
      decisionSheetApi.transition(sheetId, action),
    onSuccess: (_data, action) => {
      void queryClient.invalidateQueries({ queryKey: ['decision-sheet', sheetId] });
      void queryClient.invalidateQueries({ queryKey: decisionSheetKeys.list(projectId) });
      toast.success(t(`decisionSheet.transitioned.${action}`));
    },
    onError: (error) => {
      const problems = readNotReady(error);
      if (problems) setOpenProblems(problems);
      else toast.error(errorMessage(error, t));
    },
  });

  /**
   * "Tải JSON" — bản hiện tại lấy từ `export.json` (server biết đủ, kể cả thứ
   * view không mang); đang xem quá khứ thì dựng từ chính view, vì `export.json`
   * không nhận mốc và tệp tải về phải đúng là thứ trên màn hình.
   */
  const download = useMutation({
    mutationFn: async (view: DecisionSheetView) =>
      downloadDecisionFile(
        asOf ? sheetViewToFile(view) : await decisionSheetApi.exportFile(sheetId),
      ),
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  /**
   * "Xuất thành tài liệu dự án" — APM-DSHEET-e + APM-DSX, plan §6.1. The file
   * (`.chilldoc` via `writeChilldoc` — the server has no TipTap — or `.md`) is
   * built here at the org's timezone, then goes up presigned like every other
   * project document (rule #3).
   *
   * Sổ chưa đóng: chặn trước khi tải gì lên (nút xuất, rồi `exportSheetToDocument`
   * kiểm lại). Server vẫn trả `DECISION_SHEET_NOT_CLOSED` nếu ai đó mở lại sổ giữa
   * chừng — khi ấy cùng hộp lỗi, và tài liệu vừa tạo là một bản thừa sổ không trỏ tới.
   */
  const exportDoc = useMutation({
    mutationFn: ({ view, format }: { view: DecisionSheetView; format: DecisionExportFormat }) =>
      exportSheetToDocument(
        view,
        format,
        sheetToExportFile(format, view, new Date(), orgTimezone(), sheetDocLabels(t)),
        t('decisionSheet.exportDoc.changeNote'),
      ),
    onSuccess: (result) => {
      setChoosingFormat(false);
      queryClient.setQueryData(documentKeys.detail(result.document.id), result.document);
      void queryClient.invalidateQueries({ queryKey: ['decision-sheet', sheetId] });
      void queryClient.invalidateQueries({ queryKey: ['document'] });
      void queryClient.invalidateQueries({ queryKey: projectFilesKeys.list(projectId) });
      toast.success(
        result.created
          ? t('decisionSheet.exportDoc.created')
          : t('decisionSheet.exportDoc.versioned', { version: result.version }),
      );
    },
    onError: (error) => {
      const blocked = error instanceof SheetNotClosedError ? error.status : readNotClosed(error);
      if (!blocked) {
        toast.error(errorMessage(error, t));
        return;
      }
      setChoosingFormat(false);
      setExportBlocked(blocked);
      void queryClient.invalidateQueries({ queryKey: ['decision-sheet', sheetId] });
    },
  });

  /** Nút xuất luôn bấm được; sổ chưa đóng ⇒ hộp lỗi, không tải tệp nào lên. */
  const startExport = (view: DecisionSheetView): void => {
    if (view.status === 'closed') setChoosingFormat(true);
    else setExportBlocked(view.status);
  };

  /** "Đóng sổ" trong hộp lỗi ⇒ đóng, rồi mở tiếp hộp chọn định dạng. */
  const closeThenExport = (): void =>
    transition.mutate('close', {
      onSuccess: () => {
        // Trang thấy ngay sổ đã đóng — không đợi refetch, kẻo bấm "Xuất" trước khi nó về.
        queryClient.setQueryData<DecisionSheetView>(detailKey, (old) =>
          old ? { ...old, status: 'closed' } : old,
        );
        setExportBlocked(null);
        setChoosingFormat(true);
      },
    });

  /** "Xoá sổ" — mọi trạng thái (người dùng 2026-10-07); API xoá mềm ở mọi trạng thái. */
  const removeSheet = useMutation({
    mutationFn: () => decisionSheetApi.remove(sheetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: decisionSheetKeys.list(projectId) });
      toast.success(t('decisionSheet.editor.sheetDeleted'));
      onBack();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const back = (
    <Button size="sm" variant="ghost" className="self-start" onClick={onBack}>
      <ArrowLeft aria-hidden />
      {t('decisionSheet.back')}
    </Button>
  );

  if (sheet.isPending) {
    return (
      <div className="flex flex-col gap-3">
        {back}
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (sheet.isError) {
    return (
      <div className="flex flex-col gap-3">
        {back}
        <ErrorState message={errorMessage(sheet.error, t)} onRetry={() => void sheet.refetch()} />
      </div>
    );
  }

  const view = sheet.data;
  const historical = asOf !== undefined;
  const locked = historical || view.status !== 'open' || !view.can.answer;
  const canManage = view.can.edit && !historical;
  const totals = decisionTotals(view.questions);
  const respondents = linkRespondents(view.questions);
  const sections = decisionSections(view, filter);
  const deciderNames = view.deciders.map((person) => person.name).join(', ');
  // Sổ đóng thì mọi lệnh sửa bị chặn (`assertSheetOpen`) — không mở trình soạn.
  const canEditStructure = canManage && view.status !== 'closed';
  const editing =
    !standalone &&
    canEditStructure &&
    (requestedMode ?? (view.status === 'draft' ? 'edit' : 'view')) === 'edit';

  /*
    Nút đầu trang — một mảng, mỗi nút một phần tử (`SheetAction`). Thêm nút mới
    (Chia sẻ, Giao việc trả lời…) là thêm một phần tử ở đây. Thứ tự trong mảng
    là thứ tự trên màn hình; nút chuyển trạng thái sổ (Mở / Đóng / Mở lại) để
    cuối vì nó là nút chính.
  */
  const actions: SheetAction[] = [
    {
      id: 'download',
      label: t('decisionSheet.download'),
      icon: Download,
      variant: 'ghost',
      disabled: download.isPending,
      onClick: () => download.mutate(view),
    },
    ...(canManage
      ? [
          {
            id: 'export-doc',
            label: exportDoc.isPending
              ? t('decisionSheet.exportDoc.exporting')
              : t('decisionSheet.exportDoc.action'),
            icon: FileOutput,
            disabled: exportDoc.isPending,
            // Luôn bấm được (người dùng 2026-10-07): sổ chưa đóng thì hộp lỗi nói vì sao.
            onClick: () => startExport(view),
          },
        ]
      : []),
    ...(view.can.recordRaid && !historical && totals.answered > 0
      ? [
          {
            id: 'record-raid',
            label: t('decisionSheet.raid.action'),
            icon: Gavel,
            onClick: () => setRecordingRaid(true),
          },
        ]
      : []),
    ...(view.can.assignTask && !historical
      ? [
          {
            id: 'assign-task',
            label: t('decisionSheet.assign.action'),
            icon: UserCheck,
            onClick: () => setAssigning(true),
          },
        ]
      : []),
    ...(canManage
      ? [
          {
            id: 'deciders',
            label: t('decisionSheet.answerers.action'),
            icon: Users,
            onClick: () => setEditingDeciders(true),
          },
          ...TRANSITIONS[view.status].map((action) => ({
            id: `transition-${action}`,
            label: t(`decisionSheet.transition.${action}`),
            variant: action === 'close' ? ('outline' as const) : ('default' as const),
            disabled: transition.isPending,
            /*
              Luôn bấm được (người dùng 2026-10-07): sổ chưa đủ điều kiện thì server
              trả 409 DECISION_SHEET_NOT_READY kèm lý do, và hộp "Chưa mở được sổ"
              liệt kê từng lý do cùng nút đi sửa. Không đoán ở web — điều kiện mở có
              cả link "Trả lời" còn hiệu lực, thứ trang sổ không giữ sẵn.
            */
            onClick: () => transition.mutate(action),
          })),
          /*
            Xoá sổ — cuối hàng, ở cả Xem lẫn Sửa và mọi trạng thái (người dùng
            2026-10-07). Trước đây nằm trong `SheetMetaEditor`, tức chỉ ở chế độ
            Sửa — mà sổ đã đóng không vào được Sửa, nên không xoá được.
          */
          {
            id: 'delete',
            label: t('decisionSheet.editor.deleteSheet'),
            icon: Trash2,
            variant: 'ghost' as const,
            danger: true,
            disabled: removeSheet.isPending,
            onClick: () =>
              confirm.ask({
                message: t(
                  view.status === 'open'
                    ? 'decisionSheet.editor.deleteSheetConfirmOpen'
                    : 'decisionSheet.editor.deleteSheetConfirm',
                  { title: view.title },
                ),
                onConfirm: () => removeSheet.mutate(),
              }),
          },
        ]
      : []),
  ];

  /** Công tắc Xem | Sửa — chỉ người sửa được sổ thấy, và không khi xem quá khứ. */
  const modeSwitch =
    view.can.edit && !historical ? (
      <GlassSegmented<DecisionSheetMode>
        size="sm"
        ariaLabel={t('decisionSheet.editor.modeLabel')}
        value={editing ? 'edit' : 'view'}
        onChange={setMode}
        options={[
          { value: 'view', label: t('decisionSheet.editor.modeView') },
          {
            value: 'edit',
            label: t('decisionSheet.editor.modeEdit'),
            disabled: !canEditStructure,
            title: canEditStructure ? undefined : t('decisionSheet.editor.closedNoEdit'),
          },
        ]}
      />
    ) : null;

  const notice = historical
    ? null
    : view.status === 'closed'
      ? t('decisionSheet.closedNotice')
      : view.status === 'draft'
        ? view.can.edit
          ? t('decisionSheet.draftNotice')
          : null
        : !view.can.answer
          ? view.deciders.length > 0
            ? t('decisionSheet.onlyDeciders', { names: deciderNames })
            : t('decisionSheet.onlyDecidersNone')
          : null;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {standalone ? null : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {back}
          <div className="flex flex-wrap items-center gap-2">
            {!editing && !historical ? (
              <Button
                size="sm"
                variant="ghost"
                data-testid="decision-sheet-open-window"
                onClick={() => window.open(`/sheet/${sheetId}`, '_blank', 'noopener')}
              >
                <ExternalLink aria-hidden />
                {t('decisionSheet.openWindow')}
              </Button>
            ) : null}
            {modeSwitch}
          </div>
        </div>
      )}

      <header className="flex flex-col gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('decisionSheet.eyebrow')}
        </p>
        {editing ? (
          <SheetMetaEditor view={view} />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="min-w-0 text-xl font-semibold [overflow-wrap:anywhere]">
                {view.title}
              </h2>
              <SheetStatusPill status={view.status} />
            </div>
            {view.description ? (
              <p className="whitespace-pre-line text-sm text-muted-foreground [overflow-wrap:anywhere]">
                {view.description}
              </p>
            ) : null}
          </>
        )}

        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{t('decisionSheet.legend')}:</span>
          <OriginPill origin="documented" />
          <OriginPill origin="new" />
          <RecommendedPill />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="flex min-w-0 items-center gap-1.5 text-sm">
            <Users aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <span className="[overflow-wrap:anywhere]">
              {view.deciders.length > 0
                ? t('decisionSheet.decidersLine', { names: deciderNames })
                : t('decisionSheet.noDeciders')}
            </span>
          </span>
          {standalone ? null : <SheetActionBar actions={actions} />}
        </div>
        {respondents.length > 0 ? (
          <p
            className="flex min-w-0 items-start gap-1.5 text-sm"
            data-testid="decision-link-respondents"
          >
            <Link2 aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <span className="[overflow-wrap:anywhere]">
              {t('decisionSheet.linkRespondents.line')}{' '}
              {respondents.map((person, index) => (
                <span key={person.email || person.name} title={person.email || undefined}>
                  {index > 0 ? ', ' : null}
                  {t('decisionSheet.linkRespondents.item', {
                    name: person.name,
                    count: person.count,
                  })}
                </span>
              ))}
            </span>
          </p>
        ) : null}

        {/* Một dòng cho mỗi định dạng đã xuất — mỗi định dạng một tài liệu riêng (APM-DSX). */}
        {EXPORT_FORMATS.map((format) => {
          const documentId = exportIds[format];
          const stamp = exportStampOf(exported[format].data);
          const suffix = format === 'md' ? '-md' : '';
          if (documentId && stamp) {
            return (
              <p
                key={format}
                className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground"
                data-testid={`decision-sheet-exported${suffix}`}
              >
                <FileText aria-hidden className="size-4 shrink-0" />
                <span>
                  {t(
                    format === 'md'
                      ? 'decisionSheet.exportDoc.exportedAtMd'
                      : 'decisionSheet.exportDoc.exportedAtChilldoc',
                    { at: stampDateTime(stamp.at), version: stamp.version },
                  )}
                </span>
                <span aria-hidden>·</span>
                <button
                  type="button"
                  className="font-medium text-primary underline-offset-2 hover:underline"
                  onClick={() => setViewingExport(documentId)}
                >
                  {t('decisionSheet.exportDoc.open')}
                </button>
              </p>
            );
          }
          if (documentId && isGone(exported[format].error)) {
            return (
              <p
                key={format}
                className="text-xs text-muted-foreground"
                data-testid={`decision-sheet-exported${suffix}-gone`}
              >
                {t(
                  format === 'md'
                    ? 'decisionSheet.exportDoc.goneMd'
                    : 'decisionSheet.exportDoc.gone',
                )}
              </p>
            );
          }
          return null;
        })}
        <DecisionSheetTaskList view={view} historical={historical} />
      </header>

      {historical ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 rounded-DEFAULT border border-rag-amber/40 bg-rag-amber/10 px-3 py-2 text-sm"
        >
          <Clock aria-hidden className="size-4 shrink-0" />
          <span>{t('decisionSheet.asOfNotice', { at: stampDateTime(asOf) })}</span>
          {onAsOfChange ? (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto"
              onClick={() => onAsOfChange(undefined)}
            >
              {t('decisionSheet.asOfExit')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {notice ? (
        <p
          role="note"
          className="rounded-DEFAULT border border-border bg-muted/40 px-3 py-2 text-sm"
        >
          {notice}
        </p>
      ) : null}

      {editing ? (
        <DecisionSheetEditor view={view} />
      ) : (
        <>
          {beforeQuestions}
          <DecisionTotalsBar totals={totals} />

          {/* Cửa sổ riêng bỏ thanh lọc (người dùng 2026-10-07) — mục lục bên cạnh thay việc ấy. */}
          {standalone ? null : (
            <div className="sticky top-0 z-10 -mx-1 flex flex-wrap items-center gap-2 bg-background/90 px-1 py-2 backdrop-blur">
              <GlassSegmented
                size="sm"
                ariaLabel={t('decisionSheet.filter.label')}
                value={filter}
                onChange={setFilter}
                options={(respondents.length > 0
                  ? [...DECISION_FILTERS, 'link' as const]
                  : DECISION_FILTERS
                ).map((value) => ({
                  value,
                  label: t(`decisionSheet.filter.${value}`),
                }))}
              />
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto"
                onClick={() =>
                  document.getElementById(RESULT_ANCHOR)?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                {t('decisionSheet.viewResult')}
              </Button>
            </div>
          )}

          {view.questions.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('decisionSheet.empty')}</p>
          ) : sections.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('decisionSheet.emptyFiltered')}</p>
          ) : (
            sections.map((section) => (
              <section
                key={section.group?.id ?? 'ungrouped'}
                className="flex flex-col gap-3 not-first:mt-6"
                data-testid={`decision-group-${section.group?.code ?? 'ungrouped'}`}
              >
                <DecisionGroupHeading
                  as="h3"
                  code={section.group?.code ?? null}
                  title={section.group?.title ?? t('decisionSheet.ungrouped')}
                  highlight={section.group?.highlight ?? null}
                  note={section.group?.note ?? null}
                  answered={section.answered}
                  total={section.total}
                />

                {section.questions.map((question) => (
                  <DecisionQuestionCard
                    key={question.id}
                    question={question}
                    allowOther={view.allowOther}
                    locked={locked}
                    saving={saving.has(question.id)}
                    conflictToken={conflicts[question.id] ?? 0}
                    glass={standalone}
                    onAnswer={(key, note) => answer.mutate({ kind: 'answer', question, key, note })}
                    onClear={() => answer.mutate({ kind: 'clear', question })}
                    onHistory={historical ? undefined : () => setHistoryOf(question)}
                  />
                ))}
              </section>
            ))
          )}

          <DecisionResult view={view} id={RESULT_ANCHOR} />
        </>
      )}

      {historyOf ? (
        <AnswerHistoryDialog
          sheetId={sheetId}
          question={historyOf}
          onViewAt={
            onAsOfChange
              ? (at) => {
                  setHistoryOf(null);
                  onAsOfChange(at);
                }
              : undefined
          }
          onClose={() => setHistoryOf(null)}
        />
      ) : null}

      {viewingExport ? (
        <DocumentPreviewDialog
          documentId={viewingExport}
          projectId={view.projectId}
          canEdit={view.can.edit}
          // Deleting the document is the documents area's job, not the sheet's.
          canDelete={false}
          onClose={() => setViewingExport(undefined)}
        />
      ) : null}

      {choosingFormat ? (
        <ExportFormatDialog
          exports={{
            chilldoc: exportStampOf(exported.chilldoc.data),
            md: exportStampOf(exported.md.data),
          }}
          pending={exportDoc.isPending}
          onExport={(format) => exportDoc.mutate({ view, format })}
          onOpenDocument={(format) => {
            const documentId = exportIds[format];
            if (documentId) setViewingExport(documentId);
          }}
          onClose={() => setChoosingFormat(false)}
        />
      ) : null}

      {exportBlocked ? (
        <ExportNotClosedDialog
          status={exportBlocked}
          closing={transition.isPending}
          onCloseSheet={closeThenExport}
          onClose={() => setExportBlocked(null)}
        />
      ) : null}

      {confirm.popover}

      {assigning ? (
        <AssignAnswerTaskDialog view={view} onClose={() => setAssigning(false)} />
      ) : null}

      {recordingRaid ? (
        <RaidRecordDialog
          view={view}
          onClose={() => setRecordingRaid(false)}
          onOpenRaid={onOpenRaid}
        />
      ) : null}

      {editingDeciders ? (
        <DecidersDialog
          // Thành viên của **dự án chứa sổ** — URL chép tay có thể lệch `projectId` đang chọn.
          projectId={view.projectId}
          sheetId={sheetId}
          current={view.deciders}
          canShare={view.can.share === true}
          intro={openAfterDeciders ? t('decisionSheet.openNeedsDeciders') : undefined}
          onOpen={openAfterDeciders ? () => transition.mutate('open') : undefined}
          onClose={() => {
            setEditingDeciders(false);
            setOpenAfterDeciders(false);
          }}
        />
      ) : null}

      {openProblems ? (
        <OpenProblemsDialog
          problems={openProblems}
          canEdit={view.can.edit}
          onAddQuestions={() => setMode('edit')}
          onChooseAnswerers={() => {
            setOpenAfterDeciders(true);
            setEditingDeciders(true);
          }}
          onClose={() => setOpenProblems(null)}
        />
      ) : null}
    </div>
  );
}
